import {
  RadioGroupControl,
  SectionTitle,
  TextV2,
} from '@insights-ltd/design-library/components';
import { useTranslation } from 'react-i18next';
import { ARBITRARY_MAX_LENGTH } from 'variables';
import TextField from '@mui/material/TextField';
import LockIcon from '@mui/icons-material/Lock';
import { Box, styled } from '@mui/material';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { TeamVisibility } from 'api';

type AddToTeamProps = {
  setTeamType: (value: AddToTeamType) => void;
  teamType: AddToTeamType;
};

export type AddToTeamType = 'new-team' | 'existing-team';

const StyledLockIcon = styled(LockIcon)(({ theme }) => ({
  fill: theme.palette.pink.dark,
  height: '20px',
}));

const sxP = {
  fontWeight: '600 !important',
  lineHeight: '28px !important',
  marginTop: '0 !important',
  marginBottom: '16px !important',
};
const StyledTextField = styled(TextField)(({ theme }) => ({
  marginTop: `${theme.spacing(spacingSizeMap.S)} !important`,
  fontWeight: theme.typography.fontWeightBold,
}));

export const AddToTeamOptions = ({ setTeamType, teamType }: AddToTeamProps) => {
  const { t } = useTranslation();

  const teamTypeOptions: { label: string; value: AddToTeamType }[] = [
    {
      label: t('ui.event-management.add-to-team.radio.new-team'),
      value: 'new-team',
    },
    {
      label: t('ui.event-management.add-to-team.radio.existing-team'),
      value: 'existing-team',
    },
  ];
  return (
    <>
      <TextV2 component="p" variant="bodyBold" sx={{ marginBottom: '1rem' }}>
        {t(
          'ui.event-management.events.learners.set-reminder-modal-schedule.label-select-option',
        )}
      </TextV2>
      <RadioGroupControl<AddToTeamType>
        name="new-or-existing-team-selector"
        title="selector"
        variant="boxed"
        options={teamTypeOptions}
        onChange={(option) => {
          setTeamType(option);
        }}
        value={teamType}
      />
    </>
  );
};

export const NewTeamFields = ({
  errors,
  register,
  selectedVisibility,
  handleVisibilityChange,
}: {
  errors: Record<string, any>;
  register: any;
  selectedVisibility: TeamVisibility;
  handleVisibilityChange: (value: TeamVisibility) => void;
}) => {
  const { t } = useTranslation();

  return (
    <>
      <div>
        <StyledTextField
          id="name"
          label={t('ui.event-management.teams.team-name')}
          placeholder={t('ui.event-management.teams.team-name-placeholder')}
          required
          variant="outlined"
          fullWidth
          type="text"
          slotProps={{ htmlInput: { maxLength: ARBITRARY_MAX_LENGTH } }}
          error={Boolean(errors.name)}
          helperText={
            errors.name &&
            t(
              'ui.event-management.events.create.error.team-name-value-required',
            )
          }
          {...register('name', { required: true })}
        />
      </div>
      {/* Visibility permissions */}
      <div>
        <Box
          sx={{
            mb: spacingSizeMap.L,
          }}
        >
          <SectionTitle
            icon={{ icon: <StyledLockIcon /> }}
            text={t('ui.event-management.teams.view-permissions')}
          />
        </Box>
        <TextV2 sx={sxP} component="p" variant="bodyBold">
          {t('ui.event-management.teams.view-permissions.view-help-text')}
        </TextV2>
        <RadioGroupControl<TeamVisibility>
          options={[
            {
              value: 'PRIVATE',
              label: t('ui.event-management.teams.permissions.option-private'),
            },
            {
              value: 'ORGANISATION',
              label: t(
                'ui.event-management.teams.permissions.option-organisation',
              ),
            },
          ]}
          name="visibility"
          title={t('ui.event-management.users.create.permission-roles')}
          value={selectedVisibility}
          onChange={handleVisibilityChange}
          error={Boolean(errors.visibility)}
          variant="boxed"
        />
      </div>
    </>
  );
};
